/**
 * Vetted techniques for drawing and animating a graphic, loaded on demand via load_skill; the index
 * lives in the html-composition skill, so only what is relevant enters context.
 *
 * Kept out of the Toolbox skill store because these are written to the HtmlAsset composition
 * contract (one paused GSAP timeline, --l5-* tokens). Shares only the Skill shape.
 */
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import { HTML_DESIGN_ANIMATION_SKILLS } from './definitions';
import { HTML_DESIGN_SKILL_CATEGORIES, HTML_DESIGN_SKILL_META } from './skillMeta';

import type { CodeoSkill } from './types';

export function getHtmlDesignSkills(): CodeoSkill[] {
    return HTML_DESIGN_ANIMATION_SKILLS;
}

export function findHtmlDesignSkill(name: string): CodeoSkill | undefined {
    return HTML_DESIGN_ANIMATION_SKILLS.find((skill) => skill.name === name);
}

export function getSkillsSystemPromptSection(): string {
    const skills = getHtmlDesignSkills();
    if (skills.length === 0) {
        return '';
    }

    const formatLine = (skill: CodeoSkill): string => {
        const meta = HTML_DESIGN_SKILL_META[skill.name];
        const tags = meta?.tags ?? [];
        const tagSuffix = tags.length > 0 ? ` _(${tags.join(', ')})_` : '';
        // The index is scanned in full on every design turn, so it carries the one-clause summary
        // rather than the skill's full description. A skill missing metadata still gets a line.
        return `- ${skill.name}: ${meta?.indexSummary ?? skill.description}${tagSuffix}`;
    };

    // Group skills under their category headers, in the category order declared in skillMeta. Any
    // skill missing metadata (a registry test guards against this) still appears, under a trailing
    // "Other" group, so it never vanishes from the index.
    const seen = new Set<string>();
    const groups = HTML_DESIGN_SKILL_CATEGORIES.map(({ id, label }) => {
        const inGroup = skills.filter(
            (skill) => HTML_DESIGN_SKILL_META[skill.name]?.category === id,
        );
        inGroup.forEach((skill) => seen.add(skill.name));
        if (inGroup.length === 0) {
            return '';
        }
        return `**${label}**\n${inGroup.map(formatLine).join('\n')}`;
    }).filter(Boolean);

    const ungrouped = skills.filter((skill) => !seen.has(skill.name));
    if (ungrouped.length > 0) {
        groups.push(`**Other**\n${ungrouped.map(formatLine).join('\n')}`);
    }

    return `
## Design & motion skills

A library of vetted techniques for **drawing** the graphic and for **animating** it, grouped by what a moment is *about*. Each skill is a working implementation already rewritten to the composition contract above (one paused timeline seeked by progress, --l5-* tokens with fallbacks, GSAP-only motion) plus the craft details that make the technique read as premium. The italic keywords after each are content cues — match them to the moment you're building (a stat → Numbers & data, a scene change → Camera & transitions, a car or a clock → Drawing objects & symbols).

${groups.join('\n\n')}

Before you author or edit the graphic, decide which techniques it calls for and call \`${TOOL_NAMES.loadSkill}\` with each relevant skill's exact name to read its implementation. Do this after the plan is approved and before writing the composition — not after lint complains. Adapt the loaded snippet's selectors, times, and values to your graphic, and don't reinvent a technique a skill already implements.

**\`${TOOL_NAMES.writePlan}\` chooses for you.** An approved plan returns \`techniques.required\` — one or two skills chosen for the frame you just described, each with a note on what it does for it. Those are the techniques the graphic is built from; load them and implement them. Scan the groups above when you need something the assignment didn't cover — a drawn object, a transition between phases — and load that too, in the same turn.

There is no such thing as a graphic that needs none of this. A composition authored with nothing loaded is the library's most common failure mode, and it comes out flat in a way that reads as unfinished rather than as restraint.

**Anything the graphic depicts has to be drawn, and drawing it is not a skipped step.** There is no icon library, no symbol set, and no clip art in this environment — so a plant, a car, a figure, a clock, a shield, a server, a calendar, a hand, or any other object the brief calls for is one you construct in SVG yourself. That construction is the part that most often comes out broken: unrecognisable shapes, detail that misses the edge it belongs to, parts that scale in from the wrong corner. **Whenever the graphic will contain a drawn object or symbol, load the Drawing objects & symbols skills before you author it** — the same way you would load a motion technique. Reaching for freehand SVG without them is the single most reliable way to produce a graphic that has to be thrown away.

Within that group, \`${SKILL_NAMES.objectConstructionMethod}\` is **not optional** — it is the whole method, and it decides whether the thing reads as the thing at all. \`${SKILL_NAMES.iconSetConsistency}\` is an addition on top of it, for when the graphic holds more than one object; the rest of the group are finishing styles for a shape that method has already got right. If you are drawing anything at all, load the construction method first.

Skills are for what you are adding to the graphic — drawn objects as much as motion. When the request is a change to an existing composition that introduces no new technique — copy, numbers, colors, sizes, timing values — skip \`${TOOL_NAMES.loadSkill}\` entirely and edit directly.`;
}

/** An unregistered name passes: a skill we do not know is not evidence its technique is missing. */
export function documentUsesSkill(skillName: string, html: string): boolean {
    return findHtmlDesignSkill(skillName)?.documentUsesSkill(html) ?? true;
}
